import { graphqlQuery } from '@/lib/api/graphql';
import {
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  computePaginationWithCount,
  createDefaultPagination,
  type GraphQLConnection,
  type PaginationResult,
  pageToCursor,
} from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { Transaction, TransactionStatus, TransactionType } from '@/types/transaction';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- GraphQL node type for promise transactions ---

interface PromiseTransactionGraphQLNode {
  promiseId: number;
  promiseInvoiceId: number;
  promisedAmount: number;
  promiseInvoiceStatus: string | null;
  promiseBalanceRemaining: number;
  transactionId: number;
  referenceNumber: string | null;
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  transactionDate: string;
  dueDate: string | null;
  anticipatedDate: string | null;
  amount: number;
  netAmount: number | null;
  vatAmount: number | null;
  outstandingAmount: number;
  description: string | null;
  transactionNotes: string | null;
  headerReference: string | null;
  accountCode: string | null;
  onDispute: boolean;
  hasDispute: boolean;
  subLedger: string | null;
  subLedgerName: string | null;
  orderNumber: string | null;
  documentUrl: string | null;
  transactionReference: string | null;
  bankReference: string | null;
  externalId: string | null;
  currencyAmount: number | null;
  currencyNetAmount: number | null;
  currencyVatAmount: number | null;
  currencyOutstandingAmount: number | null;
  displayAmount: number | null;
  displayNetAmount: number | null;
  displayVatAmount: number | null;
  displayOutstandingAmount: number | null;
  displayPromisedAmount: number | null;
  daysOverdue: number;
  transactionCreatedAt: string;
  transactionModifiedAt: string | null;
  currencyCode: string | null;
  transactionTypeCode: string | null;
  transactionTypeName: string | null;
  transactionStatusCode: string | null;
  transactionStatusName: string | null;
  paymentMethodName: string | null;
  createdByName: string | null;
  modifiedByName: string | null;
  ageingBucket: string | null;
  totalAllocated: number;
  allocationCount: number;
  allocationRef: string | null;
  allocationStatusCode: string | null;
  allocationStatusName: string | null;
  hasPromise: boolean;
  hasCase: boolean;
}

interface PromiseTransactionListGraphQLResponse {
  promiseTransactions: {
    promiseTransactionList: GraphQLConnection<PromiseTransactionGraphQLNode>;
  };
}

interface PromiseTransactionCountGraphQLResponse {
  promiseTransactions: {
    promiseTransactionCount: number;
  };
}

// --- GraphQL queries ---

const PROMISE_TRANSACTION_LIST_QUERY = `
  query PromiseTransactionList($first: Int, $after: String, $sort: [PromiseTransactionListSortSortByInput!], $filter: PromiseTransactionListFilterInput) {
    promiseTransactions {
      promiseTransactionList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            promiseId
            promiseInvoiceId
            promisedAmount
            promiseInvoiceStatus
            promiseBalanceRemaining
            transactionId
            referenceNumber
            customerId
            customerName
            accountNumber
            transactionDate
            dueDate
            anticipatedDate
            amount
            netAmount
            vatAmount
            outstandingAmount
            description
            transactionNotes
            headerReference
            accountCode
            onDispute
            hasDispute
            subLedger
            subLedgerName
            orderNumber
            documentUrl
            transactionReference
            bankReference
            externalId
            currencyAmount
            currencyNetAmount
            currencyVatAmount
            currencyOutstandingAmount
            displayAmount
            displayNetAmount
            displayVatAmount
            displayOutstandingAmount
            displayPromisedAmount
            daysOverdue
            transactionCreatedAt
            transactionModifiedAt
            currencyCode
            transactionTypeCode
            transactionTypeName
            transactionStatusCode
            transactionStatusName
            paymentMethodName
            createdByName
            modifiedByName
            ageingBucket
            totalAllocated
            allocationCount
            allocationRef
            allocationStatusCode
            allocationStatusName
            hasPromise
            hasCase
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const PROMISE_TRANSACTION_COUNT_QUERY = `
  query PromiseTransactionCount($filter: PromiseTransactionListFilterInput) {
    promiseTransactions {
      promiseTransactionCount(filter: $filter)
    }
  }
`;

// --- Sort field map ---

const promiseSortFieldMap: Record<string, string> = {
  referenceNumber: 'ReferenceNumber',
  transactionDate: 'TransactionDate',
  dueDate: 'DueDate',
  anticipatedDate: 'AnticipatedDate',
  customerName: 'CustomerName',
  transactionType: 'TransactionTypeName',
  amount: 'Amount',
  outstandingAmount: 'OutstandingAmount',
  promisedAmount: 'PromisedAmount',
  status: 'TransactionStatusCode',
  currency: 'CurrencyCode',
  description: 'Description',
  createdBy: 'CreatedByName',
  modifiedBy: 'ModifiedByName',
  accountCode: 'AccountCode',
  netAmount: 'NetAmount',
  vatAmount: 'VatAmount',
  onDispute: 'OnDispute',
  subLedger: 'SubLedger',
  orderNumber: 'OrderNumber',
  currencyGross: 'CurrencyAmount',
  currencyNet: 'CurrencyNetAmount',
  currencyVAT: 'CurrencyVatAmount',
  currencyOutstanding: 'CurrencyOutstandingAmount',
};

// --- Mapping ---

function mapTransactionTypeCode(code: string | null): TransactionType {
  const typeMap: Record<string, TransactionType> = {
    invoice: 'invoice',
    credit_note: 'credit_note',
    receipt: 'receipt',
    debit_adjustment: 'debit_adjustment',
    credit_adjustment: 'credit_adjustment',
  };
  return typeMap[(code ?? '').toLowerCase()] ?? 'invoice';
}

function mapTransactionStatusCode(code: string | null): TransactionStatus {
  const statusMap: Record<string, TransactionStatus> = {
    open: 'outstanding',
    paid: 'completed',
    overdue: 'overdue',
  };
  return statusMap[(code ?? '').toLowerCase()] ?? 'outstanding';
}

export interface PromiseTransaction extends Transaction {
  promiseInvoiceId: number;
  promisedAmount: number;
  displayPromisedAmount?: number;
  promiseInvoiceStatus: string | null;
  promiseBalanceRemaining: number;
}

function mapGraphQLNodeToPromiseTransaction(node: PromiseTransactionGraphQLNode): PromiseTransaction {
  return {
    id: node.transactionId,
    referenceNumber: node.referenceNumber || String(node.transactionId),
    transactionDate: node.transactionDate,
    customerId: node.customerId,
    customerName: node.customerName ?? '',
    transactionType: mapTransactionTypeCode(node.transactionTypeCode),
    amount: node.amount,
    currency: node.currencyCode ?? 'GBP',
    status: mapTransactionStatusCode(node.transactionStatusCode),
    description: node.description ?? '',
    notes: node.transactionNotes ?? undefined,
    createdBy: node.createdByName ?? '',
    modifiedBy: node.modifiedByName ?? undefined,
    createdAt: node.transactionCreatedAt,
    modifiedAt: node.transactionModifiedAt ?? undefined,
    dueDate: node.dueDate ?? undefined,
    anticipatedDate: node.anticipatedDate ?? undefined,
    headerReference: node.headerReference ?? undefined,
    paymentMethod: node.paymentMethodName ?? undefined,
    documentUrl: node.documentUrl ?? undefined,
    outstandingAmount: node.outstandingAmount,
    onDispute: node.onDispute,
    hasDispute: node.hasDispute,
    accountCode: node.accountCode ?? undefined,
    netAmount: node.netAmount ?? undefined,
    vatAmount: node.vatAmount ?? undefined,
    subLedger: node.subLedger ?? undefined,
    subLedgerName: node.subLedgerName ?? undefined,
    orderNumber: node.orderNumber ?? undefined,
    transactionReference: node.transactionReference ?? undefined,
    bankReference: node.bankReference ?? undefined,
    externalId: node.externalId ?? undefined,
    currencyGross: node.currencyAmount ?? undefined,
    currencyNet: node.currencyNetAmount ?? undefined,
    currencyVAT: node.currencyVatAmount ?? undefined,
    currencyOutstanding: node.currencyOutstandingAmount ?? undefined,
    displayAmount: node.displayAmount ?? undefined,
    displayNetAmount: node.displayNetAmount ?? undefined,
    displayVatAmount: node.displayVatAmount ?? undefined,
    displayOutstandingAmount: node.displayOutstandingAmount ?? undefined,
    accountNumber: node.accountNumber ?? undefined,
    daysOverdue: node.daysOverdue,
    ageingBucket: node.ageingBucket ?? '',
    hasPromise: node.hasPromise,
    hasCase: node.hasCase,
    allocationStatusCode: node.allocationStatusCode ?? '',
    allocationStatusName: node.allocationStatusName ?? '',
    totalAllocated: node.totalAllocated,
    allocationCount: node.allocationCount,
    allocationRef: node.allocationRef ?? undefined,
    // Promise-specific fields
    promiseInvoiceId: node.promiseInvoiceId,
    promisedAmount: node.promisedAmount,
    displayPromisedAmount: node.displayPromisedAmount ?? undefined,
    promiseInvoiceStatus: node.promiseInvoiceStatus,
    promiseBalanceRemaining: node.promiseBalanceRemaining,
  };
}

// --- Filter builder ---

export interface GetPromiseTransactionsParams {
  promiseId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function buildPromiseTransactionFilter(
  promiseId: string | undefined,
  params?: GetPromiseTransactionsParams,
): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (promiseId) {
    filter.promiseId = { eq: Number(promiseId) };
  }

  if (params?.search) {
    filter.summary = params.search;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Result type ---

export interface PromiseTransactionsGraphQLResult {
  transactions: PromiseTransaction[];
  pagination: PaginationResult;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// --- Hook ---

const defaultPagination: PaginationResult = createDefaultPagination(25);

interface PromiseTransactionsListResult {
  transactions: PromiseTransaction[];
  pagination: PaginationResult;
}

export function usePromiseTransactionsPaginatedGraphQL(
  promiseId: string | undefined,
  params?: GetPromiseTransactionsParams,
): PromiseTransactionsGraphQLResult {
  const apiConfig = useApiConfig();

  const queryParams: GetPromiseTransactionsParams = {
    page: 1,
    pageSize: 25,
    sortBy: 'transactionDate',
    sortOrder: 'desc',
    ...params,
  };

  const page = queryParams.page ?? 1;
  const pageSize = queryParams.pageSize ?? 25;

  const mockListResult: PromiseTransactionsListResult = {
    transactions: [],
    pagination: computeMockPagination(0, page, pageSize),
  };

  const filter = buildPromiseTransactionFilter(promiseId, queryParams);

  // --- List query ---
  const listQuery = useModeAwareQuery<PromiseTransactionsListResult, Error>(
    ['promise-transactions-graphql-list', promiseId, queryParams],
    async ({ signal }) => {
      if (!promiseId) {
        return { transactions: [], pagination: computeMockPagination(0, 1, pageSize) };
      }

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildSort(promiseSortFieldMap, queryParams.sortBy, queryParams.sortOrder),
        filter,
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<PromiseTransactionListGraphQLResponse>(
        apiConfig,
        PROMISE_TRANSACTION_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.promiseTransactions.promiseTransactionList;
      const transactions = connection.edges.map((x) => mapGraphQLNodeToPromiseTransaction(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: transactions.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { transactions, pagination };
    },
    mockListResult,
    {
      enabled: !!promiseId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  // --- Count query ---
  const countQuery = useModeAwareQuery<number, Error>(
    ['promise-transactions-graphql-count', promiseId, filter],
    async ({ signal }) => {
      if (!promiseId) return 0;

      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<PromiseTransactionCountGraphQLResponse>(
        apiConfig,
        PROMISE_TRANSACTION_COUNT_QUERY,
        variables,
        signal,
      );

      return response.promiseTransactions.promiseTransactionCount;
    },
    0,
    {
      enabled: !!promiseId,
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );

  // --- Combine results ---
  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    transactions: listQuery.data?.transactions ?? [],
    pagination,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
    },
  };
}

// --- Export filter builder (for useExportAll) ---

export function buildPromiseTransactionExportFilter(
  promiseId: string | undefined,
  search?: string,
): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (promiseId) {
    filter.promiseId = { eq: Number(promiseId) };
  }

  if (search) {
    filter.summary = search;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Column name map for export ---

export const PROMISE_TRANSACTION_EXPORT_COLUMN_MAP: Record<string, string> = {
  referenceNumber: 'ReferenceNumber',
  transactionDate: 'TransactionDate',
  dueDate: 'DueDate',
  anticipatedDate: 'AnticipatedDate',
  transactionType: 'TransactionTypeName',
  amount: 'Amount',
  currency: 'CurrencyCode',
  outstandingAmount: 'OutstandingAmount',
  promisedAmount: 'PromisedAmount',
  status: 'TransactionStatusName',
  description: 'Description',
  accountCode: 'AccountCode',
  netAmount: 'NetAmount',
  vatAmount: 'VatAmount',
  subLedger: 'SubLedger',
  orderNumber: 'OrderNumber',
  createdBy: 'CreatedByName',
  modifiedBy: 'ModifiedByName',
  customerName: 'CustomerName',
  onDispute: 'OnDispute',
  flags: 'Flags',
};
